// Top-of-page banners: DeletedBanner (assistant sees), AnonymizedBanner,
// UpsellOnlyBanner (P1 — student delivery not live yet).

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../../ui';
import { CHANNEL, FEATURE_FLAG } from '../../constants';
import { useFeatures } from '../../../settings/hooks/useSettingsState';

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const Warn = styled(Banner)`
  background: ${({ theme }) => theme.colors.MARKER_LIGHT};
  color: ${({ theme }) => theme.colors.MARKER};
`;

const Muted = styled(Banner)`
  background: ${({ theme }) => theme.colors.NEU_100};
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' }); } catch { return iso; }
};

export const DeletedBanner = ({ deletedAt }) => {
  const { t } = useTranslation();
  if (!deletedAt) return null;
  return (
    <Muted>
      <Icon.Info size='small' />
      {t('campaigns.banner.deleted', 'This campaign was soft-deleted on {{when}}. Only assistants and dark-admins can see it.', { when: formatDate(deletedAt) })}
    </Muted>
  );
};

export const AnonymizedBanner = ({ anonymizedAt }) => {
  const { t } = useTranslation();
  if (!anonymizedAt) return null;
  return (
    <Muted>
      <Icon.Info size='small' />
      {t('campaigns.banner.anonymized', 'Recipient details were anonymized on {{when}} per retention policy. Aggregate metrics are preserved.', { when: formatDate(anonymizedAt) })}
    </Muted>
  );
};

// Shown for network-upsell-only campaigns while the student-facing integration
// is behind a feature flag (see UX doc §7A).
export const UpsellOnlyBanner = ({ campaign }) => {
  const { t } = useTranslation();
  const features = useFeatures();
  const live = !!features?.[FEATURE_FLAG.NETWORK_UPSELL_LIVE];
  const upsellOnly = campaign?.channels?.length === 1 && campaign.channels.includes(CHANNEL.NETWORK_UPSELL);
  if (!upsellOnly || live) return null;
  return (
    <Warn>
      <Icon.Info size='small' />
      {t('campaigns.banner.upsellNotLive', 'This campaign is authored but not yet delivered to students. Student-facing activation will enable in a future release.')}
    </Warn>
  );
};
